import { Field, FieldArray, Form, Formik } from "formik";
import isEmpty from "lodash/isEmpty";
import noop from "lodash/noop";
import PropTypes from "prop-types";
import React from "react";
import { Button, Col, FormText, Row } from "react-bootstrap";
import ParamUtils, { PRIORITY } from "ui/parameter-form/utils";
import { sortByKeyAsc } from "util/sort-by-prop";
import { Box } from "view/layout/box";

import { CustomParamFormField } from "./custom-parameter-form-field";
import Overrides from "./overrides";

const defaultCustomParam = Object.freeze({
  k: "",
  v: "",
  errors: [],
  hasOverride: false,
  isOverriding: false,
  isDuplicate: false,
  isLegacy: false,
});

class CustomParameterForm extends React.PureComponent {
  static get propTypes() {
    return {
      deviceParams: PropTypes.arrayOf(PropTypes.object),
      legacyParams: PropTypes.instanceOf(Set),
      network: PropTypes.object,
      networkParams: PropTypes.arrayOf(PropTypes.object),
      readOnly: PropTypes.bool.isRequired,
      priority: PropTypes.oneOf([PRIORITY.DEVICE, PRIORITY.NETWORK]).isRequired,
    };
  }

  static get defaultProps() {
    return {
      deviceParams: [],
      legacyParams: new Set(),
      networkParams: [],
    };
  }

  constructor(props) {
    super(props);

    this.form = null;
    this.initialValues = this._getInitialParams();

    this._excludeUnsetParams = this._excludeUnsetParams.bind(this);
    this._onOverride = this._onOverride.bind(this);
    this._renderCustomParams = this._renderCustomParams.bind(this);
    this._setRef = this._setRef.bind(this);
  }

  _excludeUnsetParams(param) {
    // For params that have lower priority options and are not overridden,
    // they need to be excluded on submission.
    // These params are unset at current priority level, e.g.
    // a device param with toggle button off.
    const { hasOverride, isOverriding } = param;
    return !(hasOverride && !isOverriding);
  }

  _excludeInvalidParams(param) {
    const { k, isDuplicate, isLegacy } = param;
    return !isDuplicate && !isLegacy && Boolean(k);
  }

  _getInitialParams() {
    const { deviceParams, legacyParams, networkParams, priority } = this.props;
    // We should hide legacy params from UI during the transition to V3.
    const allParams = [...deviceParams, ...networkParams].filter(
      (p) => !legacyParams.has(p.name),
    );

    const initialParams = allParams.reduce((acc, { name }) => {
      const deviceParam = ParamUtils.getDeviceLevelParam(name, deviceParams);
      const networkParam = ParamUtils.getNetworkLevelParam(name, networkParams);
      const priorities = ParamUtils.buildPriorities(
        null,
        networkParam,
        deviceParam,
      );
      const isOverriding = Overrides.isOverriding(priority, priorities);
      const hasOverride = Overrides.hasOverride(priority, priorities);

      acc[name] = {
        ...defaultCustomParam,
        k: name,
        v: ParamUtils.getPriorityValue(name, { deviceParams, networkParams }),
        isLegacy: legacyParams.has(name),
        hasOverride,
        isOverriding,
      };
      acc[name].errors = ParamUtils.getErrorMsg(acc[name]);

      return acc;
    }, {});

    return { params: Object.values(initialParams).sort(sortByKeyAsc) };
  }

  _onOverride(field, isOverriding) {
    const priority = isOverriding ? PRIORITY.MAX : this.props.priority;

    this.resetFieldValue(field, isOverriding, priority);
  }

  getParams() {
    const formikBag = this.form.getFormikBag();
    const { params } = formikBag.values;

    return params
      .filter(this._excludeUnsetParams)
      .filter(this._excludeInvalidParams);
  }

  resetFieldValue(field, isOverriding, priority = PRIORITY.MAX) {
    const { deviceParams, networkParams } = this.props;
    const { k } = field.value;

    const initialValue = ParamUtils.getPriorityValue(k, {
      deviceParams,
      networkParams,
      priority,
    });
    const fieldValue = {
      ...field.value,
      v: initialValue,
      isOverriding,
    };

    // Possible the ref is not ready.
    if (this.form) {
      this.form.setFieldValue(field.name, fieldValue);
    }
  }

  _renderAddParamButton(hasParameter, onAdd) {
    const { readOnly } = this.props;

    return (
      <Button
        id="addButton"
        className="pull-right"
        variant={hasParameter ? "primary" : "secondary"}
        disabled={readOnly}
        onClick={onAdd}
      >
        <i className="fa fa-plus" /> Add Parameter
      </Button>
    );
  }

  _renderCustomParams(arrayHelper) {
    const { legacyParams, network, networkParams, priority, readOnly } =
      this.props;
    const { form, push, remove } = arrayHelper;
    const { values } = form;

    const hasParameter = !isEmpty(values.params);
    const onAdd = () => push(defaultCustomParam);

    return (
      <div>
        {hasParameter ? (
          <Box noHeader={true}>
            {values.params.map((param, idx) => {
              return (
                <Col key={`param-${idx}`} xs={12}>
                  <Field name={`params.${idx}`}>
                    {({ field, form }) => (
                      <CustomParamFormField
                        field={field}
                        form={form}
                        legacyParams={legacyParams}
                        network={network}
                        networkParams={networkParams}
                        onOverride={this._onOverride}
                        onRemove={() => remove(idx)}
                        priority={priority}
                        readOnly={readOnly}
                      />
                    )}
                  </Field>
                </Col>
              );
            })}
          </Box>
        ) : (
          <FormText>No Custom Parameters are set.</FormText>
        )}
        <Row>
          <Col xs={12}>{this._renderAddParamButton(hasParameter, onAdd)}</Col>
        </Row>
      </div>
    );
  }

  _setRef(el) {
    if (el) {
      this.form = el;
    }
  }

  render() {
    return (
      <div>
        <Row>
          <Col xs={12}>
            <Formik
              initialValues={this.initialValues}
              onSubmit={noop}
              ref={this._setRef}
            >
              <Form>
                <FieldArray name="params" render={this._renderCustomParams} />
              </Form>
            </Formik>
          </Col>
        </Row>
      </div>
    );
  }
}

export { defaultCustomParam, CustomParameterForm };
